<template>
  <v-container
    grid-list-xl
    fluid
  >
    <v-layout
      row
      wrap
    >
      <v-flex xs12>
        <FilterButtons
          :filters="appliedFilters"
          @filter-removed="removeFilter"
        />

        <CollectionFilters
          :filters="filters"
          :active-filters="appliedFilters"
          @filters-submitted="submitFilters"
          @filters-cleared="clearFilters"
        />
      </v-flex>
      <v-flex xs12>
        <v-card>
          <v-card-title class="justify-end">
            <v-flex
              xs12
              md6
              class="py-0"
            >
              <v-text-field
                :loading="loading"
                v-model="search"
                :label="$t('Search by product name')"
                hide-details
                append-icon="search"
              />
            </v-flex>
          </v-card-title>
          <!--          <pre>{{ items }}</pre>-->
          <v-data-table
            ref="dTable"
            :items="items"
            :headers="headers"
            :loading="loading"
            :pagination.sync="pagination"
            :total-items="meta.totalItems"
            :rows-per-page-text="$t('Rows per page')"
            :rows-per-page-items="meta.rowsPerPageItems"
            :expand="true"
          >
            <v-progress-linear
              slot="progress"
              color="primary"
              indeterminate
            />
            <template v-slot:items="props" >
              <product-inventory-row
                v-model="props.expanded"
                :item="props.item"
                :key="props.item.id"
              />
            </template>

            <template slot="no-data">
              <v-btn
                color="primary"
                @click="clearFilters"
              >
                {{ $t('Reset') }}
              </v-btn>
            </template>
            <template v-slot:pageText="props">
              <span>{{ $t('{0} - {1} of {2}', [props.pageStart, props.pageStop, props.itemsLength]) }}</span>
            </template>
            <template v-slot:expand="props">
              <div v-if="props.item.children && props.item.children.length > 0">
                <table
                  v-if="getInventoriableChildren(props.item).length > 0"
                  style="width:100%;
                   table-layout: fixed;
                   overflow-wrap: break-word;
                  "
                >
                  <tbody
                    v-for="item in getInventoriableChildren(props.item)"
                    :key="item.id"
                  >
                    <product-inventory-row
                      :item="item"
                      :key="props.item.id"
                      :parent="props.item"
                      :is-child="true"
                      @updateParent="updateParent"
                    />
                  </tbody>
                </table>
              </div>
            </template>
          </v-data-table>
        </v-card>
      </v-flex>
    </v-layout>
  </v-container>
</template>

<script>
import { createNamespacedHelpers } from 'vuex'
import debounce from 'lodash.debounce'
import ProductInventoryRow from '@/modules/Products/components/Inventory/ProductInventoryRow'
import CollectionFilters from '@/vuetify-console/components/CollectionFilters'
import FilterButtons from '@/vuetify-console/components/FilterButtons'

const { mapActions, mapGetters } = createNamespacedHelpers('catalog/products', { include: 'children' })

export default {
  name: 'ProductsInventory',
  components: { ProductInventoryRow, CollectionFilters, FilterButtons },
  data () {
    return {
      customOpt: {
        where: ['classification', 'eq', 'common'],
        include: ['children']
      },
      items: [],
      preloaded: false,
      currentRow: null,
      headers: [
        { text: this.$t('SKU'), value: 'sku' },
        { text: this.$t('Sort Order'), value: 'order' },
        { text: this.$t('Name'), value: 'name' },
        { text: this.$t('Current Qty'), value: 'inventory.quantity' },
        { text: this.$t('New Qty'), value: '', sortable: false },
        { text: this.$t('Adjust'), value: 'adjust', sortable: false }
      ]
    }
  },

  computed: {
    ...mapGetters(['collection', 'loading', 'meta', 'appliedFilters']),

    pagination: {
      get: function () {
        return this.meta
      },
      set: async function (value) {
        if (this.preloaded) {
          this.applyMeta(value, false)
          await this.fetchCollection(this.customOpt)
          this.items = [...this.collection]
        } else {
          await this.fetchCollection(this.customOpt)
          this.items = [...this.collection]
          this.preloaded = true
        }
      }
    },

    filters () {
      return [
        { field: 'sku', type: 'string', label: this.$t('SKU') },
        { field: 'name', type: 'string', label: this.$t('Name') },
        { field: 'inventory.quantity', type: 'number', label: this.$t('Quantity') }
      ]
    },

    nameFilter () {
      return (this.appliedFilters || []).find(filter => filter.field === 'name')
    },

    search: {
      set: debounce(function (value) {
        let filters = [...this.appliedFilters || []]

        if (!value) {
          filters = filters.filter(filter => filter.field !== 'name')
          this.submitFilters(filters)
          return
        }

        const filter = filters.find(f => f.field === 'name')
        if (filter) {
          filter.value = value
          this.submitFilters(filters)
          return
        }

        filters.push({
          label: this.$t('Name'),
          field: 'name',
          type: 'string',
          operator: 'like',
          value
        })
        this.submitFilters(filters)
      }, 500),
      get () {
        return this.nameFilter ? this.nameFilter.value : ''
      }
    }
  },

  watch: {
    items: function () {
      this.expandAll()
    }
  },

  methods: {
    ...mapActions(['applyMeta', 'removeFilter', 'clearFilters', 'fetchCollection']),

    updateParent (parent, child) {
      parent.children = parent.children.map(i => i.id === child.id ? child : i)
    },

    getInventoriableChildren (item) {
      return item.children.filter(i => i.inventory.track || i.inventory.is_restockable)
    },

    expandAll () {
      if (this.items.length) {
        this.items.forEach((item) => {
          this.$set(this.$refs.dTable.expanded, item.id, true)
        })
      }
    },

    async submitFilters (filters) {
      this.applyMeta({ filters }, false)
      await this.fetchCollection(this.customOpt)
      this.items = [...this.collection]
    }
  }
}
</script>
